import axios from "axios";
import {
  CREATE_ADVERT_URL,
  GET_JOB_PROFILE_DETAILS_URL,
} from "@/utils/constants/constants.js";
import createAdvertModalHelpersMixin from "./create_advert_modal_helpers_mixin";

export default {
  name: "CreateAdvertModal",
  mixins: [createAdvertModalHelpersMixin],
  props: {
    headers: { type: Object, required: true },
    entityTypeId: { type: [Number, String], required: true },
    entityTypeName: { type: String, default: "" },
    openNonce: { type: Number, default: 0 },
  },
  emits: ["advert-created", "advert-error"],
  data() {
    return {
      dragDisplayIndex: null,
      activeRichEditor: null,
      advertLoadingProfile: false,
      advertProfile: null,
      advertSaving: false,
      createAdvertData: this.buildEmptyCreateAdvertData(),
    };
  },
  watch: {
    openNonce() {
      this.initializeCreateAdvertModal();
    },
    entityTypeId() {
      if (this.openNonce) {
        this.initializeCreateAdvertModal();
      }
    },
  },
  methods: {
    async initializeCreateAdvertModal() {
      this.resetCreateAdvertState();
      const entityTypeId = Number(this.entityTypeId);
      if (!Number.isFinite(entityTypeId) || entityTypeId <= 0) {
        this.$emit("advert-error", "Unable to create advert: missing job profile.");
        return;
      }

      const openNonce = this.openNonce;
      this.advertLoadingProfile = true;
      await axios.get(GET_JOB_PROFILE_DETAILS_URL, {
        headers: this.headers,
        params: { entity_type_id: entityTypeId },
      }).then((response) => {
        if (openNonce === this.openNonce) {
          this.advertProfile = this.buildAdvertProfile(response);
        }
      }).catch((error) => {
        if (openNonce === this.openNonce) {
          this.advertProfile = null;
          this.$emit("advert-error", error.response?.data?.message || error.message || "Failed to load job profile.");
        }
      }).finally(() => {
        if (openNonce === this.openNonce) {
          this.advertLoadingProfile = false;
        }
      });
    },
    addCreateAdvertSection() {
      if (!Array.isArray(this.createAdvertData.sections)) {
        this.createAdvertData.sections = [];
      }
      this.createAdvertData.sections.push(this.buildEmptyAdvertSection());
      this.rebuildCreateDisplayOrder();
    },
    addCreateAdvertSubsection(sectionIndex) {
      const section = this.getCreateAdvertSection(sectionIndex);
      if (!section) return;
      if (!Array.isArray(section.subsections)) {
        section.subsections = [];
      }
      this.clearSectionContentForSubsections(section);
      section.subsections.push(this.buildEmptyAdvertSubsection());
    },
    removeCreateAdvertSubsection(sectionIndex, subsectionIndex) {
      const section = this.getCreateAdvertSection(sectionIndex);
      if (!Array.isArray(section?.subsections)) return;
      if (subsectionIndex < 0 || subsectionIndex >= section.subsections.length) return;
      section.subsections.splice(subsectionIndex, 1);
    },
    removeCreateAdvertSection(index) {
      if (!Array.isArray(this.createAdvertData.sections)) return;
      if (index < 0 || index >= this.createAdvertData.sections.length) return;
      this.createAdvertData.sections.splice(index, 1);
      this.rebuildCreateDisplayOrder();
    },
    onCreateDisplayDragStart(index) {
      this.dragDisplayIndex = index;
    },
    onCreateDisplayDragEnd() {
      this.dragDisplayIndex = null;
    },
    onCreateDisplayDrop(targetIndex) {
      if (!Array.isArray(this.createAdvertData.display_order)) return;
      const sourceIndex = this.dragDisplayIndex;
      if (!this.hasValidCreateDisplayDrop(sourceIndex, targetIndex)) {
        this.dragDisplayIndex = null;
        return;
      }
      const list = [...this.createAdvertData.display_order];
      const [moved] = list.splice(sourceIndex, 1);
      list.splice(targetIndex, 0, moved);
      this.createAdvertData.display_order = list;
      this.dragDisplayIndex = null;
    },
    async emitSaveAdvert() {
      if (this.activeRichEditor) {
        this.activeRichEditor.dispatchEvent(new Event("blur", { bubbles: true }));
      }
      this.rebuildCreateDisplayOrder();
      if (!this.createAdvertData.title || !this.createAdvertData.title.trim()) {
        this.$emit("advert-error", "Advert title is required.");
        return;
      }
      if (!this.createAdvertData.reference_number || !String(this.createAdvertData.reference_number).trim()) {
        this.$emit("advert-error", "Reference number is required.");
        return;
      }
      if (!this.validateAdvertDateRange(this.createAdvertData.opening_date, this.createAdvertData.closing_date)) {
        return;
      }

      this.advertSaving = true;
      await axios.post(CREATE_ADVERT_URL, this.buildCreateAdvertPayload(), {
        headers: this.headers,
        withCredentials: true,
      }).then((response) => {
        if (response.data?.status !== "success") {
          throw new Error(response.data?.message || "Unexpected response");
        }
        this.handleCreateAdvertSuccess();
      }).catch((error) => {
        this.$emit("advert-error", error.response?.data?.message || error.message || "Failed to create advert.");
      }).finally(() => {
        this.advertSaving = false;
      });
    },
  },
};
